import React from "react";
import { Tag } from "antd";
import changelog from "data/ai-changelog.json";

// GENERATED CONTENT. This page renders src/data/ai-changelog.json, which is
// produced from pm/CHANGELOG.md by pm/changelog/to_json.py in the HV repo.
// Do NOT hand-edit either the JSON or the copy below: pm/ is the single source
// of truth, and a second hand-maintained copy is exactly the drift this project
// has already been bitten by.

// Colour is a secondary cue only. Every group also carries its name as text, so
// the meaning survives a colourblind reader or a monochrome print. Contrast is
// kept at the 700/300 ends of each ramp rather than the 500 midpoints, which do
// not clear 4.5:1 on white.
const TYPE_STYLES = {
  Added: "bg-emerald-50 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
  Changed: "bg-sky-50 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200",
  Deprecated: "bg-amber-50 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200",
  Removed: "bg-orange-50 text-orange-900 dark:bg-orange-900/40 dark:text-orange-200",
  Fixed: "bg-violet-50 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200",
  Security: "bg-rose-50 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200",
};

const ChangeGroup = ({ type, entries }) => (
  <div className="mt-3 first:mt-0">
    <span
      className={`inline-block rounded px-2 py-0.5 text-xs font-semibold tracking-wide ${
        TYPE_STYLES[type] || "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
      }`}
    >
      {type}
    </span>
    <ul className="mt-2 ml-1 space-y-1.5 list-disc list-inside marker:text-gray-400">
      {entries.map((entry, i) => (
        <li
          key={i}
          className="text-sm leading-relaxed text-slate-700 dark:text-gray-300"
        >
          {entry}
        </li>
      ))}
    </ul>
  </div>
);

const Release = ({ release, isCurrent }) => (
  <section
    data-testid={`release-${release.version}`}
    aria-label={
      release.unreleased
        ? `${release.version} changes, not released yet`
        : `Version ${release.version}, released ${release.date}`
    }
    className={`rounded-xl border bg-white dark:bg-gray-800 p-4 sm:p-5 ${
      release.unreleased
        ? "border-dashed border-gray-400 dark:border-gray-500"
        : "border-gray-200 dark:border-gray-700"
    }`}
  >
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
        {release.version}
      </h3>
      {/* No date is printed for unreleased work. Inventing one would be a lie,
          and a blank slot invites someone to fill it in. */}
      {release.unreleased ? (
        <Tag color="default">Not released yet</Tag>
      ) : (
        /* Tabular figures so the dates line up down the page instead of drifting. */
        <time
          dateTime={release.date}
          className="text-sm text-gray-500 dark:text-gray-400 tabular-nums"
        >
          {release.date}
        </time>
      )}
      {isCurrent && <Tag color="green">Current</Tag>}
      {release.milestones && release.milestones !== "none." && (
        <span className="text-xs text-gray-500 dark:text-gray-400">
          Milestones: {release.milestones}
        </span>
      )}
    </div>

    {release.note && (
      <p className="mt-3 border-l-2 border-amber-300 dark:border-amber-500 pl-3 text-sm italic text-slate-600 dark:text-gray-400">
        {release.note}
      </p>
    )}

    <div className="mt-3">
      {Object.entries(release.changes).map(([type, entries]) => (
        <ChangeGroup key={type} type={type} entries={entries} />
      ))}
    </div>
  </section>
);

const Changelog = () => {
  const releases = changelog.releases || [];

  return (
    <div>
      <div className="mb-4">
        <p className="text-sm text-slate-600 dark:text-gray-400">
          Version history for the AI workstream only, not the HyvidOne app and not
          the wider team's releases. Ordered by version, newest first, following{" "}
          <span className="font-medium">Keep a Changelog</span> and{" "}
          <span className="font-medium">Semantic Versioning</span>.
        </p>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
          Generated from <code>pm/CHANGELOG.md</code>. Last updated {changelog.updated}.
          Dates are the day the work landed, so they are not always in step with the
          version order.
        </p>
      </div>

      {releases.length === 0 ? (
        <div
          data-testid="changelog-empty"
          className="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 p-8 text-center"
        >
          <p className="text-sm text-slate-600 dark:text-gray-400">
            No releases recorded yet.
          </p>
          <p className="mt-1 text-xs text-gray-500">
            Add one to <code>pm/CHANGELOG.md</code>, then run{" "}
            <code>python3 pm/changelog/to_json.py</code>.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {releases.map((release) => (
            <Release
              key={release.version}
              release={release}
              isCurrent={release.version === changelog.current_version}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Changelog;
